import { t } from 'i18n';
import {
  Button,
  CustomLink,
  styled,
  SubTitle,
  useToast,
} from '@teko/ds-desktop';
import { logger } from '@teko/log';
import {
  getTrackingData,
  AddToCartType,
  TrackAddToCartParams,
} from '@teko/tracking';
import { useProductDetailState, useTenantConfig, useCartV2 } from 'contexts';
import { needConfirm18YearsOld } from 'helpers/confirm18YearsOld';
import { trackAddToCart } from '@teko/tracking';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useState } from 'react';
import { setCookieOver18YearOld } from '@teko/utils';
import { CautionOutlined } from '@teko/ds-icon';
import dynamic from 'next/dynamic';

const DynamicCheckAgeModalContent = dynamic(() =>
  import('../../shared/CheckAgeModalContent').then(
    (mod) => mod.CheckAgeModalDesktop,
  ),
);

export const ActionButtons: React.FC = () => {
  // TODO: use these 2 below states to catch message from Cart
  // when addToCart product having license
  // const [licenseWarningMessage, setLicenseWarningMessage] = useState('');
  // const [showLicenseWarningModal, setShowLicenseWarningModal] = useState(false);
  const [showPopupCheckAge, setShowPopupCheckAge] = useState(false);
  const router = useRouter();
  const {
    product,
    primitiveProduct,
    isOutOfStock,
    isCanAddToCard,
    stockAvailable,
    price,
    payloadAddToCart,
    selectedPromotion,
  } = useProductDetailState();
  const { contactLink } = useTenantConfig();
  const showToast = useToast();
  const { onAddSingleItem } = useCartV2();
  const isValidPrice = useMemo(() => {
    if (!product) return false;
    if (price) return price?.finalPrice && price?.finalPrice >= 0;
  }, [product, price]);
  const showContact =
    !isValidPrice || isOutOfStock || !product?.status?.sellable;

  useEffect(() => {
    if (needConfirm18YearsOld(product?.isAlcoholicCategory || false)) {
      setShowPopupCheckAge(true);
    }
  }, [product?.sku, product?.isAlcoholicCategory]);

  const trackingPayload = JSON.stringify({
    sku: product?.sku,
    screenName: 'productDetail',
  });

  const onAddItemToCart = async (type: AddToCartType) => {
    if (!isCanAddToCard) {
      showToast.info?.({
        title: '',
        message: t('Chỉ còn {{stockAvailable}} sản phẩm', {
          stockAvailable: stockAvailable,
        }),
        type: 'error',
        icon: <CautionOutlined size={50} color="#E24C4B" />,
      });
      return;
    }
    if (!primitiveProduct) return;
    // Note: Remove function passLicenseCheck: logic check product's license at client
    // Cart will check this (instead of us)
    // task: https://jira.teko.vn/browse/CART21-994

    // params send to Teko tracking in event addToCart
    const trackAddToCartParams: TrackAddToCartParams = {
      product: { ...primitiveProduct, addToCartPrice: price?.finalPrice || 0 },
      selectedPromotionId: selectedPromotion?.promotionId,
      type,
    };

    try {
      if (!payloadAddToCart?.sku) return;
      const res = await onAddSingleItem({
        sku: payloadAddToCart?.sku,
        promotionId: selectedPromotion?.promotionId,
        quantity: payloadAddToCart?.quantity,
        isPrimary: payloadAddToCart?.isPrimary,
      });
      if (res) {
        trackAddToCart({
          ...trackAddToCartParams,
          status: 'success',
          cartId: res.id,
        });
      } else {
        trackAddToCart({ ...trackAddToCartParams, status: 'failed' });
      }

      if (res && type === AddToCartType.BUYNOW) router.push('/cart');
    } catch (e) {
      trackAddToCart({ ...trackAddToCartParams, status: 'failed' });
      logger.error(e);
    }
  };

  return (
    <>
      {!showContact && (
        <ButtonWrapper>
          <BuyNowButtonWrapper
            {...getTrackingData({
              isTrackContent: true,
              contentName: 'buyNow',
              regionName: 'descriptionDetail',
              target: 'cart',
              payload: trackingPayload,
            })}
          >
            <StyledButton
              variant="primary"
              onClick={() => onAddItemToCart(AddToCartType.BUYNOW)}
              className="att-detail-page-buy-now-button"
              block
            >
              <ButtonText level={2} weight="medium">
                {t('Mua ngay').toUpperCase()}
              </ButtonText>
            </StyledButton>
          </BuyNowButtonWrapper>

          <AddToCartButtonWrapper
            {...getTrackingData({
              isTrackContent: true,
              contentName: 'addToCart',
              target: 'productDetail',
              regionName: 'descriptionDetail',
              payload: trackingPayload,
            })}
          >
            <StyledButton
              variant="secondary"
              onClick={() => onAddItemToCart(AddToCartType.ADDTOCART)}
              block
            >
              <ButtonText level={2} weight="medium">
                {t('THÊM VÀO GIỎ HÀNG')}
              </ButtonText>
            </StyledButton>
          </AddToCartButtonWrapper>
        </ButtonWrapper>
      )}
      {showContact && !!contactLink && (
        <CustomLink href={contactLink} target="_blank">
          <ContactSaleButtonWrapper
            {...getTrackingData({
              isTrackContent: true,
              regionName: 'descriptionDetail',
              contentName: 'contactSale',
              target: 'productDetail',
              payload: trackingPayload,
            })}
          >
            <StyledButton variant="primary" block marginTop="s16">
              <ButtonText level={2} weight="medium">
                {t('LIÊN HỆ')}
              </ButtonText>
            </StyledButton>
          </ContactSaleButtonWrapper>
        </CustomLink>
      )}
      {showContact && !contactLink && (
        <StyledButton disabled block marginTop="s16">
          <ButtonText level={2} weight="medium">
            {t('HẾT HÀNG')}
          </ButtonText>
        </StyledButton>
      )}

      {showPopupCheckAge && (
        <DynamicCheckAgeModalContent
          show={showPopupCheckAge}
          onCancel={() => router.push('/')}
          onOk={() => {
            setCookieOver18YearOld(true);
            setShowPopupCheckAge(false);
          }}
        />
      )}
    </>
  );
};
const ButtonWrapper = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
`;
const ButtonText = styled(SubTitle)`
  font-size: 14px;
`;
const StyledButton = styled(Button)`
  height: 46px;
`;

const BuyNowButtonWrapper = styled.div`
  flex: 1;
`;

const AddToCartButtonWrapper = styled.div`
  flex: 1;
`;

const ContactSaleButtonWrapper = styled.div``;
